import React, { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  DateInput,
  NumberInput,
  required,
  TopToolbar,
  ListButton,
  useNotify,
  useRedirect,
  AutocompleteInput,
  BooleanInput,
} from 'react-admin';
import { Grid, Card, CardContent, Typography, Divider, Box, Chip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  Assignment as TaskIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Schedule as ScheduleIcon,
  Flag as PriorityIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { PageTitle } from '../../components/PageTitle';
import { TaskEntitySelector } from '../../components/TaskEntitySelector';
import { TaskTagsInput } from '../../components/TaskTagsInput';
import { HoursMinutesInput } from '../../components/HoursMinutesInput';

const CreateActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
);

const statusChoices = [
  { id: 'Todo', name: 'To Do' },
  { id: 'InProgress', name: 'In Progress' },
  { id: 'InReview', name: 'In Review' },
  { id: 'Blocked', name: 'Blocked' },
  { id: 'Done', name: 'Done' },
  { id: 'Cancelled', name: 'Cancelled' },
];

const priorityChoices = [
  { id: 'Lowest', name: 'Lowest' },
  { id: 'Low', name: 'Low' },
  { id: 'Medium', name: 'Medium' },
  { id: 'High', name: 'High' },
  { id: 'Highest', name: 'Highest' },
  { id: 'Critical', name: 'Critical' },
];

const urgencyChoices = [
  { id: 'Low', name: 'Low' },
  { id: 'Normal', name: 'Normal' },
  { id: 'Urgent', name: 'Urgent' },
];



export const TaskCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const location = useLocation();

  // Pre-fill form based on URL parameters
  const [defaultValues, setDefaultValues] = useState<any>({});
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const entityType = searchParams.get('entityType');
    const entityId = searchParams.get('entityId');
    const boardId = searchParams.get('boardId');
    
    const values: any = {};
    
    if (entityType) {
      values.entityType = entityType;
      
      if (entityId) {
        values.entityId = entityId;
      }
    }
    
    // Note: boardId is not used - tasks are organized by category, not board
    
    if (Object.keys(values).length > 0) {
      setDefaultValues(values);
    }
  }, [location.search]);

  const onSuccess = (data: any) => {
    notify('Task created successfully', { type: 'success' });
    // Use the ID from the response or fallback to list
    if (data?.id) {
      redirect('show', 'tasks', data.id);
    } else {
      redirect('list', 'tasks');
    }
  };

  const onError = (error: any) => {
    console.error('Task creation error:', error);
    notify('Failed to create task. Please try again.', { type: 'error' });
  };

  return (
    <>
      <PageTitle title="Create New Task" titleKey="pages.createTask" icon={<TaskIcon />} />
      <Create 
        actions={<CreateActions />} 
        mutationOptions={{ onSuccess, onError }} 
        record={defaultValues}>
        <SimpleForm>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TaskIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextInput
                        source="title"
                        label="Task Title"
                        validate={[required()]}
                        fullWidth
                        helperText="Enter a clear, descriptive title for the task"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextInput
                        source="description"
                        label="Description"
                        multiline
                        rows={4}
                        fullWidth
                        helperText="Provide detailed information about what needs to be done"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Status and Priority */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <PriorityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Status & Priority
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SelectInput
                        source="status"
                        label="Status"
                        choices={statusChoices}
                        defaultValue="Todo"
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectInput
                        source="priority"
                        label="Priority"
                        choices={priorityChoices}
                        defaultValue="Medium"
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SelectInput
                        source="urgency"
                        label="Urgency"
                        choices={urgencyChoices}
                        defaultValue="Normal"
                        validate={[required()]}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Assignment and Category */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Assignment
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <ReferenceInput
                        source="assigneeId"
                        reference="users"
                        label="Assignee"
                      >
                        <AutocompleteInput
                          optionText="name"
                          fullWidth
                          helperText="Select a team member to assign this task"
                        />
                      </ReferenceInput>
                    </Grid>
                    <Grid item xs={12}>
                      <SelectInput
                        source="categoryId"
                        label="Category"
                        choices={[
                          { id: 'maintenance', name: 'Maintenance' },
                          { id: 'inspection', name: 'Inspection' },
                          { id: 'insurance', name: 'Insurance' },
                          { id: 'registration', name: 'Registration' },
                          { id: 'license', name: 'License' },
                          { id: 'permit', name: 'Permit' },
                          { id: 'fuel-tax', name: 'Fuel Tax' },
                          { id: 'dot-compliance', name: 'DOT Compliance' },
                          { id: 'training', name: 'Training' },
                          { id: 'warranty', name: 'Warranty' },
                          { id: 'customer-service', name: 'Customer Service' },
                          { id: 'administrative', name: 'Administrative' },
                          { id: 'sales', name: 'Sales & Marketing' },
                          { id: 'billing', name: 'Billing & Finance' },
                        ]}
                        validate={[required()]}
                        fullWidth
                        helperText="Choose the appropriate task category"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Scheduling */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Scheduling
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <DateInput
                        source="startDate"
                        label="Start Date"
                        fullWidth
                        parse={(value) => value ? new Date(value).toISOString().split('T')[0] : null}
                        format={(value) => value ? new Date(value).toISOString().split('T')[0] : ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DateInput
                        source="dueDate"
                        label="Due Date"
                        fullWidth
                        parse={(value) => value ? new Date(value).toISOString().split('T')[0] : null}
                        format={(value) => value ? new Date(value).toISOString().split('T')[0] : ''}
                        helperText="When should this task be completed?"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <HoursMinutesInput
                        source="estimatedHours"
                        label="Estimated Time"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Entity Association */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Related Entity (Optional)
                  </Typography>
                  <Grid container spacing={2}>
                    <TaskEntitySelector />
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Hierarchy */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Task Hierarchy (Optional)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <ReferenceInput
                        source="parentTaskId"
                        reference="tasks"
                        label="Parent Task"
                      >
                        <AutocompleteInput
                          optionText="title"
                          fullWidth
                          helperText="Make this a subtask of another task"
                        />
                      </ReferenceInput>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" height="100%">
                        <Typography variant="body2" color="text.secondary">
                          If you select a parent task, this will become a subtask that contributes to the parent's completion.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <TaskTagsInput source="tags" label="Tags (Optional)" />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SimpleForm>
      </Create>
    </>
  );
}; 